import React from 'react';
import { normalizeInactiveReason } from '@/utils/applicationInactiveStatus';

/** Corps du petit personnage (partagé Aucun / Mise à jour / Fin de service) */
function InactiveProtestPerson({ className, gripX = 44, gripY = 40, variant = 'protest' }) {
  return (
    <g className={className}>
      <path
        d="M30 78 L34 68 M42 78 L38 68"
        fill="none"
        stroke="#6b7280"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <ellipse cx="36" cy="62" rx="11" ry="13" fill="#6b7280" />
      <circle cx="34" cy="49" r="9" fill="#9ca3af" />
      <path d="M28 46 Q34 42 40 46" fill="#6b7280" />
      {variant === 'protest' ? (
        <>
          <path
            d="M28 58 Q22 64 20 70"
            fill="none"
            stroke="#6b7280"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d={`M40 56 L${gripX} ${gripY}`}
            fill="none"
            stroke="#9ca3af"
            strokeWidth="3.5"
            strokeLinecap="round"
          />
          <circle cx={gripX} cy={gripY} r="2.5" fill="#9ca3af" />
        </>
      ) : (
        <>
          <path
            d="M28 58 Q22 64 20 70"
            fill="none"
            stroke="#6b7280"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <g transform="translate(40, 56)">
            <g className="inactive-door-wave-arm">
              <path
                d="M0 0 L6 -10 L10 -22"
                fill="none"
                stroke="#6b7280"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="10" cy="-24" r="3" fill="#9ca3af" />
            </g>
          </g>
        </>
      )}
    </g>
  );
}

/** Aucun : personnage qui brandit un panneau sens interdit */
function NoneProtestSignAnimation() {
  return (
    <svg
      viewBox="0 0 88 84"
      className="inactive-reason-none h-[5.5rem] w-[5.25rem]"
      aria-hidden="true"
    >
      <g className="inactive-none-pole-group">
        <line
          x1="44"
          y1="40"
          x2="44"
          y2="16"
          stroke="#9ca3af"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <g className="inactive-none-sign-panel">
          <rect
            x="26"
            y="2"
            width="36"
            height="22"
            rx="2"
            fill="#374151"
            stroke="#9ca3af"
            strokeWidth="1.5"
          />
          <circle cx="44" cy="13" r="7" fill="none" stroke="#f76f4b" strokeWidth="2.5" />
          <line
            x1="38"
            y1="7"
            x2="50"
            y2="19"
            stroke="#f76f4b"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </g>
      </g>

      <InactiveProtestPerson className="inactive-none-protest-person" />
    </svg>
  );
}

/** Maintenance : personnage derrière son écran, code qui s’écrit */
function MaintenanceCoderAnimation() {
  return (
    <svg
      viewBox="0 0 112 92"
      className="inactive-reason-maint h-[5.5rem] w-[7rem]"
      aria-hidden="true"
    >
      <g className="inactive-maint-chair">
        <path d="M6 36 L20 34 L22 54 L8 56 Z" fill="#4b5563" stroke="#6b7280" strokeWidth="0.8" />
        <path d="M8 52 L28 50 L32 60 L10 62 Z" fill="#6b7280" stroke="#6b7280" strokeWidth="0.6" />
        <path d="M10 58 L12 62 L10 72" fill="none" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" />
        <path d="M28 56 L30 60 L28 70" fill="none" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" />
        <path d="M8 54 L6 58 L6 62" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" />
      </g>

      <g className="inactive-maint-person">
        <path d="M14 62 Q22 68 30 62 Q26 58 20 58 Q14 58 14 62 Z" fill="#5b6370" />
        <ellipse cx="26" cy="48" rx="11" ry="13" fill="#6b7280" transform="rotate(-12 26 48)" />
        <path d="M30 42 Q36 38 42 40" fill="none" stroke="#9ca3af" strokeWidth="4" strokeLinecap="round" />
        <ellipse cx="38" cy="33" rx="8" ry="9" fill="#9ca3af" transform="rotate(8 38 33)" />
        <path d="M32 28 Q38 22 44 27 Q42 24 38 23 Q32 23 32 28 Z" fill="#6b7280" />
      </g>

      <g className="inactive-maint-desk">
        <path d="M30 50 L100 46 L104 56 L34 60 Z" fill="#4b5563" />
        <path d="M34 60 L104 56 L102 68 L32 72 Z" fill="#374151" />
        <path d="M30 50 L34 60 L32 72 L28 62 Z" fill="#2d3748" />
      </g>

      <path d="M46 54 L74 52 L76 56 L48 58 Z" fill="#3d4450" stroke="#6b7280" strokeWidth="0.8" />
      <line x1="50" y1="55.5" x2="72" y2="54" stroke="#9ca3af" strokeWidth="0.5" opacity="0.4" />

      <path
        className="inactive-maint-arm"
        d="M32 48 Q42 52 50 56"
        fill="none"
        stroke="#6b7280"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle className="inactive-maint-hand" cx="50" cy="56" r="3.5" fill="#9ca3af" />

      <g className="inactive-maint-monitor">
        <path d="M66 30 L96 28 L96 50 L66 52 Z" fill="#374151" stroke="#9ca3af" strokeWidth="1.5" />
        <path d="M96 28 L100 26 L100 48 L96 50 Z" fill="#2d3748" stroke="#6b7280" strokeWidth="1" />
        <path d="M66 30 L100 26 L96 28 L66 30 Z" fill="#4b5563" opacity="0.35" />
        <rect x="69" y="33" width="24" height="15" fill="#0f172a" />
        <line
          className="inactive-maint-code inactive-maint-code-1"
          x1="71"
          y1="36"
          x2="91"
          y2="36"
          stroke="#34d399"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <line
          className="inactive-maint-code inactive-maint-code-2"
          x1="71"
          y1="40"
          x2="84"
          y2="40"
          stroke="#38bdf8"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <line
          className="inactive-maint-code inactive-maint-code-3"
          x1="71"
          y1="44"
          x2="88"
          y2="44"
          stroke="#a78bfa"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect className="inactive-maint-cursor" x="88" y="43" width="1.5" height="3" fill="#f76f4b" />
        <rect x="80" y="50" width="5" height="6" fill="#6b7280" />
        <rect x="74" y="56" width="16" height="2" rx="0.5" fill="#4b5563" />
      </g>
    </svg>
  );
}

/** Mise à jour : personnage avec ardoise V1 — pose initiale levée (comme motif Aucun) */
function UpdateBeforeAfterAnimation() {
  return (
    <svg
      viewBox="0 0 88 84"
      className="inactive-reason-update h-[5.5rem] w-[5.25rem]"
      aria-hidden="true"
    >
      <g className="inactive-update-scene">
        <g className="inactive-update-v1-pole-group">
          <line
            x1="44"
            y1="40"
            x2="44"
            y2="16"
            stroke="#9ca3af"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <rect
            x="26"
            y="2"
            width="36"
            height="22"
            rx="2"
            fill="#374151"
            stroke="#9ca3af"
            strokeWidth="1.5"
          />
          <text
            className="inactive-update-v1-label"
            x="44"
            y="14"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#f9fafb"
            fontSize="11"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontWeight="700"
          >
            V1
          </text>
          <text
            className="inactive-update-v2-label"
            x="44"
            y="14"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#38bdf8"
            fontSize="11"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontWeight="700"
          >
            V2
          </text>
        </g>

        <g className="inactive-update-person">
          <path
            d="M30 78 L34 68 M42 78 L38 68"
            fill="none"
            stroke="#6b7280"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <ellipse cx="36" cy="62" rx="11" ry="13" fill="#6b7280" />
          <circle cx="34" cy="49" r="9" fill="#9ca3af" />
          <path d="M28 46 Q34 42 40 46" fill="#6b7280" />
          <path
            d="M28 58 Q22 64 20 70"
            fill="none"
            stroke="#6b7280"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M40 56 L44 40"
            fill="none"
            stroke="#9ca3af"
            strokeWidth="3.5"
            strokeLinecap="round"
          />
          <circle cx="44" cy="40" r="2.5" fill="#9ca3af" />
        </g>
      </g>
    </svg>
  );
}

/** Fin de service : personnage dans le cadre fait au revoir, puis la porte se ferme */
function EndOfServiceDoorAnimation() {
  return (
    <svg
      viewBox="0 0 80 88"
      className="inactive-reason-door h-[5.5rem] w-20"
      aria-hidden="true"
    >
      <defs>
        <clipPath id="inactive-door-frame-clip">
          <rect x="14" y="10" width="52" height="68" rx="2" />
        </clipPath>
      </defs>

      <rect
        x="14"
        y="10"
        width="52"
        height="68"
        fill="#1f2937"
        stroke="#6b7280"
        strokeWidth="2"
        rx="2"
      />

      <g clipPath="url(#inactive-door-frame-clip)">
        <g className="inactive-door-wave-person">
          <InactiveProtestPerson variant="wave" />
        </g>
      </g>

      <g transform="translate(16, 12)">
        <g className="inactive-door-panel">
          <rect x="0" y="0" width="48" height="64" fill="#4b5563" stroke="#9ca3af" strokeWidth="1.5" rx="1" />
          <circle cx="42" cy="32" r="2.5" fill="#9ca3af" />
          <g transform="translate(24, 32)">
            <g className="inactive-door-forbidden">
              <circle cx="0" cy="0" r="13" fill="none" stroke="#f76f4b" strokeWidth="2.5" />
              <line
                x1="-10"
                y1="-10"
                x2="10"
                y2="10"
                stroke="#f76f4b"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}

/**
 * Illustration animée du modal d'indisponibilité, selon le motif de désactivation.
 */
export default function InactiveReasonAnimation({ reason }) {
  const normalized = normalizeInactiveReason(reason);

  switch (normalized) {
    case 'maintenance':
      return <MaintenanceCoderAnimation />;
    case 'update':
      return <UpdateBeforeAfterAnimation />;
    case 'end_of_service':
      return <EndOfServiceDoorAnimation />;
    default:
      return <NoneProtestSignAnimation />;
  }
}
